"use client";

import {
  Notification,
  Organization,
  Surgeon,
} from "@clients/velys-infrastructure/api";
import { StateCreator } from "zustand";
import { IDropdownOption } from "../components/Dropdown/Dropdown";

export interface IOrganization extends Organization {
  procedures?: string[];
}

export type GlobalState = {
  caseData: string;
  filterComponent: React.ReactNode | null;
  overlayType: string;
  modalType: string;
  isModalOpen: boolean;
  isQOpen: boolean;
  orgProfile: IOrganization | null;
  orgLocations: IDropdownOption[];
  orgSurgeons: IDropdownOption[];
  orgProcedures: IDropdownOption[];
  selectedLocale: string;
  viewingHelp: boolean;
  openNotifications: boolean;
  openAllNotifs: boolean;
  currentSheet: string | null;
  faqTab: string;
  notifications: Notification[];
  userCountry: string;
  geoError: boolean;
  isSurgeonsInitialized: boolean;
  currentNotificationClearIds: string[];
  selectedOrgId: string | null;
  userLicenses: { [key: string]: boolean } | null;
  isEditingProfile: boolean;

  // actions
  setCaseData: (newData: string) => void;
  setFilterComponent: (component: React.ReactNode | null) => void;
  setOverlayType: (type: string) => void;
  setModalType: (type: string) => void;
  setModalOpen: (open: boolean) => void;
  setQOpen: (open: boolean) => void;
  setOrgProfile: (orgProfile: IOrganization | null) => void;
  setSelectedLocale: (locale: string) => void;
  setViewingHelp: (isViewing: boolean) => void;
  setOpenNotifications: (isOpen: boolean) => void;
  setOpenAllNotifs: (allOpen: boolean) => void;
  setCurrentSheet: (sheetName: string) => void;
  setFaqTab: (tab: string) => void;
  setNotifications: (notifs: Notification[]) => void;
  setOrgSurgeons: (surgeons: Surgeon[]) => void;
  setGeoLocation: (country: string, hasError: boolean) => void;
  setIsFilterInitialized: (initialized: boolean) => void;
  addNotificationId: (id: string) => void;
  removeNotificationId: (id: string) => void;
  clearGeoLocation: () => void;
  setSelectedOrgId: (orgId: string) => void;
  setUserLicenses: (licenses: { [key: string]: boolean } | null) => void;
  setIsEditingProfile: (isEditing: boolean) => void;
};

export const createGlobalSlice: StateCreator<GlobalState> = (set) => ({
  //client state
  caseData: "newData",
  filterComponent: null,
  overlayType: "",
  modalType: "",
  isModalOpen: false,
  isQOpen: false,
  orgProfile: null,
  orgLocations: [],
  orgSurgeons: [],
  orgProcedures: [],
  selectedLocale: "",
  viewingHelp: false,
  openNotifications: false,
  openAllNotifs: false,
  currentSheet: null,
  faqTab: "insights",
  notifications: [],
  userCountry: "",
  geoError: false,
  isSurgeonsInitialized: false,
  currentNotificationClearIds: [],
  selectedOrgId: null,
  userLicenses: null,
  isEditingProfile: false,

  // actions
  setCaseData: (newData) => set({ caseData: newData }),
  setFilterComponent: (component) => set({ filterComponent: component }),
  setOverlayType: (type) => set({ overlayType: type }),
  setModalType: (type) => set({ modalType: type }),
  setQOpen: (isOpen) => set({ isQOpen: isOpen }),
  setModalOpen: (isOpen) => set({ isModalOpen: isOpen }),
  setOrgProfile: (orgProfile) =>
    set({
      orgProfile,
      orgLocations: [
        { value: "all", name: "All Locations" },
        ...(orgProfile?.locations?.map((loc) => ({
          value: loc,
          name: loc,
        })) ?? []),
      ],
      orgProcedures: [
        ...(orgProfile?.procedures?.map((proc) => ({
          value: proc,
          name: proc,
        })) ?? []),
      ],
    }),
  setSelectedLocale: (locale) => {
    set({ selectedLocale: locale });

    if (typeof window !== "undefined") {
      const isSecure = window.location.hostname !== "localhost";
      document.cookie = `user-locale-preference=${locale}; path=/; max-age=31536000; SameSite=Lax${
        isSecure ? "; Secure" : ""
      }`;
    }
  },
  setViewingHelp: (isViewing) => set({ viewingHelp: isViewing }),
  setOpenNotifications: (isOpen) => set({ openNotifications: isOpen }),
  setOpenAllNotifs: (allOpen) => set({ openAllNotifs: allOpen }),
  setCurrentSheet: (sheetName: string) => set({ currentSheet: sheetName }),
  setFaqTab: (tab: string) => set({ faqTab: tab }),
  setNotifications: (notifs: Notification[]) => set({ notifications: notifs }),
  setOrgSurgeons: (surgeons: Surgeon[]) =>
    set({
      orgSurgeons:
        surgeons.map((surgeon) => ({
          value: surgeon.npi || "",
          name: surgeon.name || "",
        })) ?? [],
    }),
  setGeoLocation: (country, hasError) =>
    set({
      userCountry: country,
      geoError: hasError,
    }),
  clearGeoLocation: () =>
    set({
      userCountry: undefined,
      geoError: undefined,
    }),
  setIsFilterInitialized: (initialized: boolean) =>
    set({ isSurgeonsInitialized: initialized }),
  addNotificationId: (id) => {
    set((state) => ({
      currentNotificationClearIds: [...state.currentNotificationClearIds, id],
    }));
  },
  removeNotificationId: (id) => {
    set((state) => ({
      currentNotificationClearIds: state.currentNotificationClearIds.filter(
        (updateId) => updateId !== id
      ),
    }));
  },
  setSelectedOrgId: (selectedOrgId) =>
    set({
      selectedOrgId,
      isSurgeonsInitialized: false,
    }),
  setUserLicenses: (userLicenses) => set({ userLicenses }),
  setIsEditingProfile: (isEditing) => set({ isEditingProfile: isEditing }),
});
